import { defineStore } from 'pinia';
import { ref } from 'vue';

/**
 * How many battle reports have arrived that nobody has looked at.
 *
 * Deliberately a client-side count rather than a server field. Read state is a
 * fact about a person sitting at a screen, not about the world, and adding a
 * per-report read flag to the store would mean writing to the world every time
 * somebody opened a page. The count survives a reload through localStorage,
 * which is as much durability as "have I seen this yet" deserves.
 *
 * Reports still arrive and are stored exactly as before; this only decides
 * whether the nav wears a dot.
 */
const KEY = 'waldermarch.unreadReports';

export const useUnreadStore = defineStore('unread', () => {
  const reports = ref(readStored());

  function readStored(): number {
    try {
      const raw = window.localStorage.getItem(KEY);
      const n = raw ? Number.parseInt(raw, 10) : 0;
      return Number.isFinite(n) && n > 0 ? n : 0;
    } catch {
      // Private browsing. The badge simply starts empty every session.
      return 0;
    }
  }

  function persist(): void {
    try {
      window.localStorage.setItem(KEY, String(reports.value));
    } catch {
      /* Nothing to be done, and nothing worth telling the player. */
    }
  }

  /** A report just landed. */
  function bump(): void {
    reports.value += 1;
    persist();
  }

  /** The player opened the Reports page, so the slate is clean. */
  function clear(): void {
    if (reports.value === 0) return;
    reports.value = 0;
    persist();
  }

  return { reports, bump, clear };
});
